import React, { useEffect, useRef, useState } from 'react';
import { getManualAPI } from '../../apis/manualFaq/manualFaqAPI';
import * as pdfjsLib from 'pdfjs-dist';
import NotFound from 'pages/NotFound';
import { Box, Button } from '@mui/material';

// PDF.js 워커 설정
pdfjsLib.GlobalWorkerOptions.workerSrc = process.env.REACT_APP_PDF_WORKER_SRC || '/pdf.worker.min.mjs';

export default function Manual() {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string>('ulvac_manual.pdf');
  const [numPages, setNumPages] = useState<number>(0);
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const isRenderingRef = useRef(false); // 렌더링 상태를 추적하는 플래그

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const fetchManual = async () => {
    try {
      const { url, filename } = await getManualAPI();
      setPdfUrl(url);
      setPdfName(filename);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setPdfUrl('');
    setPdfName('ulvac_manual.pdf');

    fetchManual();
  }, [language]);

  useEffect(() => {
    const renderPDF = async (url: string) => {
      // 이미 렌더링 중이면 새로운 렌더링 요청 무시
      if (isRenderingRef.current) return;

      // 렌더링 시작
      isRenderingRef.current = true;

      try {
        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;
        setNumPages(pdf.numPages);

        const canvasContainer = canvasContainerRef.current;
        if (canvasContainer) {
          canvasContainer.innerHTML = ''; // 기존 캔버스 제거

          // 순차적으로 페이지를 렌더링
          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            await renderPage(pdf, pageNum, canvasContainer);
          }
        }
      } finally {
        // 렌더링 완료
        isRenderingRef.current = false;
      }
    };

    const renderPage = async (pdf: any, pageNum: number, canvasContainer: HTMLDivElement) => {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 3 });

      const canvas = document.createElement('canvas');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const context = canvas.getContext('2d');

      if (context) {
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;
        canvas.style.width = '100%'; // 캔버스의 너비를 100%로 설정
        canvas.style.height = 'auto'; // 비율을 유지하면서 높이를 자동으로 설정
        canvasContainer.appendChild(canvas);
      }
    };

    if (pdfUrl) {
      renderPDF(pdfUrl);
    }
  }, [pdfUrl]);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.setAttribute('download', pdfName); // 실제 파일 이름 설정
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Box>
      {pdfUrl ? (
        <Box>
          {pdfUrl && (
            <Button onClick={handleDownload} sx={{ margin: 1 }}>
              Manual Download
            </Button>
          )}
          <div ref={canvasContainerRef} style={{ width: '100%' }}></div>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height="580px">
          <NotFound />
        </Box>
      )}
    </Box>
  );
}
